"use client";

import { flushSync } from "react-dom";

import {
  createContext,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import Link from "next/link";
import { useDocsSearchState } from "@/providers/docs-search-state";
import { DocsProductLogo } from "./product-logo";
import { Search, ArrowUpRight, FileText, CodeXml, Layers } from "lucide-react";
import { useSidebar } from "@/components/ui/sidebar";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { SaveButtonSpinner } from "@/components/save-button-spinner";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { DocsSearchEntry } from "@/lib/docs/catalog";
import { createDocsSearch } from "@/lib/docs/search";

const SearchContext = createContext<{
  open: boolean;
  openSearch: (button: HTMLButtonElement) => void;
  modifier: string;
  tabFocus: boolean;
  setTabFocus: (value: boolean) => void;
  tabNavigation: React.MutableRefObject<boolean>;
} | null>(null);

export function DocsSearchTrigger({
  iconOnly = false,
}: {
  iconOnly?: boolean;
}) {
  const state = useContext(SearchContext);
  if (!state) throw new Error("DocsSearchTrigger requires DocsSearch");
  const { open, openSearch, modifier, tabFocus, setTabFocus, tabNavigation } =
    state;
  return (
    <button
      type="button"
      aria-label="Search documentation"
      aria-haspopup="dialog"
      aria-expanded={open}
      data-docs-search-trigger
      onClick={(event) => openSearch(event.currentTarget)}
      data-tab-focus={tabFocus}
      onFocus={() => setTabFocus(tabNavigation.current)}
      onBlur={() => setTabFocus(false)}
      className={`flex shrink-0 items-center justify-center text-foreground focus-visible:outline-brand-primary data-[tab-focus=false]:!outline-none data-[tab-focus=false]:!ring-0 ${iconOnly ? "size-9 rounded-lg" : "h-9 w-full gap-2 rounded-md border-2 border-input bg-transparent px-3 text-sm dark:border-backgroundAccent"}`}
    >
      <Search className={iconOnly ? "size-5" : "size-4 shrink-0"} aria-hidden />
      {!iconOnly && (
        <>
          <span className="flex-1 text-left">Search</span>
          <span className="flex shrink-0 gap-1" aria-hidden>
            {[modifier, "K"].map((key) => (
              <kbd
                key={key}
                className="inline-flex h-5 min-w-5 items-center justify-center rounded-sm bg-sidebar-accent px-1 font-mono text-[10px] text-muted"
              >
                {key}
              </kbd>
            ))}
          </span>
        </>
      )}
    </button>
  );
}

export function DocsSearch({
  entries,
  children,
}: {
  entries: DocsSearchEntry[];
  children?: React.ReactNode;
}) {
  const { isMobile, setOpenMobile } = useSidebar();
  const {
    open,
    setOpen,
    query,
    setQuery,
    site,
    setSite,
    version,
    setVersion,
    apiResults,
    setApiResults,
  } = useDocsSearchState();
  const activeScope = site === "symx" ? "symx" : "all";
  const includeApi = site === "all" || site === "NexumAPI" || site === "symx";
  const activeVersion = activeScope === "symx" ? version : "all";
  const browsingApi = activeScope === "symx" && !query.trim();
  const apiKey = `${query.trim()}|${activeScope}|${activeVersion}`;
  const restoredOpen = useRef(open).current;
  const [modifier, setModifier] = useState("⌘");
  const input = useRef<HTMLInputElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const resultsList = useRef<HTMLUListElement>(null);
  const navigating = useRef(false);
  const [tabFocus, setTabFocus] = useState(false);
  const tabNavigation = useRef(false);
  const resultPositions = useRef(new Map<string, number>());
  useEffect(() => {
    const q = query.trim();
    if (!open || (q.length < 2 && !browsingApi) || !includeApi) return;
    const controller = new AbortController();
    const timer = setTimeout(async () => {
      try {
        const response = await fetch(
          `/api/docs/nexum/search?q=${encodeURIComponent(q)}${activeScope === "symx" ? `&scope=symx&version=${activeVersion}` : ""}`,
          { signal: controller.signal },
        );
        if (!response.ok) throw new Error("Search unavailable");
        if (!controller.signal.aborted) {
          const items: DocsSearchEntry[] = await response.json();
          if (!controller.signal.aborted)
            setApiResults({ query: apiKey, entries: items });
        }
      } catch {
        if (!controller.signal.aborted)
          setApiResults({ query: apiKey, entries: [], error: true });
      }
    }, 150);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [
    browsingApi,
    open,
    query,
    apiKey,
    activeScope,
    activeVersion,
    includeApi,
    setApiResults,
  ]);
  const siteEntries = useMemo(
    () =>
      site === "all"
        ? entries
        : entries.filter((entry) => entry.product === site),
    [entries, site],
  );
  const search = useMemo(() => createDocsSearch(siteEntries), [siteEntries]);
  const searching = query.trim().length >= 2;
  const results = searching ? search.search(query.trim(), { limit: 20 }) : [];
  const suggestions = useMemo(
    () => siteEntries.filter((entry) => !entry.href.includes("#")).slice(0, 8),
    [siteEntries],
  );
  const proseEntries =
    activeScope === "symx"
      ? []
      : searching
        ? results.map(({ item, matches }) => {
            const match = matches?.find((entry) => entry.key === "text");
            const start = Math.max(0, (match?.indices[0]?.[0] ?? 0) - 45);
            const snippet = item.text.slice(start, start + 150);
            return {
              ...item,
              excerpt: `${start ? "…" : ""}${snippet}${start + 150 < item.text.length ? "…" : ""}`,
            };
          })
        : suggestions.map((item) => ({ ...item, excerpt: "" }));

  const visibleEntries = [
    ...proseEntries.slice(
      0,
      includeApi && apiResults.query === apiKey && apiResults.entries.length
        ? 12
        : 20,
    ),
    ...(includeApi && (searching || browsingApi) && apiResults.query === apiKey
      ? apiResults.entries.map((item) => ({
          ...item,
          excerpt: item.text,
        }))
      : []),
  ].slice(0, browsingApi ? 100 : 20);
  const apiPending =
    includeApi && (searching || browsingApi) && apiResults.query !== apiKey;
  const apiFailed =
    includeApi && apiResults.query === apiKey && apiResults.error;
  const resultLayout = visibleEntries
    .map((item) => `${item.href}:${item.excerpt}`)
    .join("|");
  useLayoutEffect(() => {
    const previous = resultPositions.current;
    const next = new Map<string, number>();
    const reducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    resultsList.current
      ?.querySelectorAll<HTMLLIElement>("li")
      .forEach((item) => {
        const key = item.dataset.href!;
        const top = item.offsetTop;
        next.set(key, top);
        if (!reducedMotion && item.animate && previous.size) {
          const oldTop = previous.get(key);
          item.getAnimations().forEach((animation) => animation.cancel());
          item.animate(
            oldTop === undefined
              ? [
                  { opacity: 0, transform: "translateY(6px)" },
                  { opacity: 1, transform: "translateY(0)" },
                ]
              : [
                  { transform: `translateY(${oldTop - top}px)` },
                  { transform: "translateY(0)" },
                ],
            { duration: 220, easing: "ease-in-out" },
          );
        }
      });
    resultPositions.current = next;
  }, [resultLayout, open]);

  const changeOpen = (next: boolean) => {
    setOpen(next);
    if (!next) setQuery("");
  };
  useEffect(() => {
    setModifier(/Mac|iPhone|iPad/.test(navigator.platform) ? "⌘" : "Ctrl");
    const onKey = (event: KeyboardEvent) => {
      tabNavigation.current = event.key === "Tab";
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        navigating.current = false;
        trigger.current =
          Array.from(
            document.querySelectorAll<HTMLButtonElement>(
              "[data-docs-search-trigger]",
            ),
          ).find((button) => button.getClientRects().length > 0) ?? null;
        setOpen((value) => !value);
        setQuery("");
      }
    };
    const onPointer = () => {
      tabNavigation.current = false;
      setTabFocus(false);
    };
    window.addEventListener("keydown", onKey, true);
    window.addEventListener("pointerdown", onPointer, true);
    return () => {
      window.removeEventListener("keydown", onKey, true);
      window.removeEventListener("pointerdown", onPointer, true);
    };
  }, [setOpen, setQuery]);

  return (
    <SearchContext.Provider
      value={{
        open,
        modifier,
        tabFocus,
        setTabFocus,
        tabNavigation,
        openSearch: (button) => {
          trigger.current = button;
          navigating.current = false;
          flushSync(() => {
            if (isMobile) setOpenMobile(false);
            setOpen(true);
          });
          input.current?.focus({ preventScroll: true });
        },
      }}
    >
      <Dialog open={open} onOpenChange={changeOpen}>
        {children ?? <DocsSearchTrigger />}
        <DialogContent
          showCloseButton={false}
          className={`docs-controls flex h-[min(600px,85svh)] flex-col gap-0 overflow-hidden bg-card p-0 text-primary sm:max-w-xl ${restoredOpen ? "data-[state=open]:animate-none" : ""}`}
          onOpenAutoFocus={(event) => {
            event.preventDefault();
            input.current?.focus({ preventScroll: true });
          }}
          onCloseAutoFocus={() => {
            if (!navigating.current) {
              tabNavigation.current = false;
              setTabFocus(false);
              trigger.current?.focus({ preventScroll: true });
            }
          }}
        >
          <div className="shrink-0 px-5 pb-4 pt-5">
            <DialogTitle className="text-gray-1100 dark:text-gray-100">
              Search Libum Docs
            </DialogTitle>
            <div
              role="group"
              aria-label="Documentation site"
              className="docs-search-filters -mx-5 mt-3 flex flex-nowrap gap-1 overflow-x-auto px-5 py-1 sm:mx-0 sm:px-0"
            >
              {[
                { value: "all", label: "All", product: null },
                {
                  value: "PowerOn Pro",
                  label: "PowerOn Pro",
                  product: "poweron-pro",
                },
                {
                  value: "PowerOn Pipelines",
                  label: "PowerOn Pipelines",
                  product: "poweron-pipelines",
                },
                { value: "NexumAPI", label: "NexumAPI", product: "nexum" },
                { value: "Glaci", label: "Glaci", product: "glaci" },
                { value: "symx", label: "SymX API", product: null },
              ].map(({ value, label, product }) => (
                <button
                  key={value}
                  type="button"
                  aria-label={
                    value === "symx" ? "SymXchange API Reference" : label
                  }
                  aria-pressed={site === value}
                  onClick={() => setSite(value)}
                  className="inline-flex h-7 shrink-0 select-none items-center justify-center gap-1 whitespace-nowrap rounded-md border border-transparent bg-sidebar-accent px-2 text-[11px] font-medium text-foreground transition-colors duration-200 ease-in-out hover:bg-[color-mix(in_srgb,hsl(var(--sidebar-accent))_85%,hsl(var(--foreground))_15%)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary aria-pressed:border-brand-primary motion-reduce:transition-none dark:aria-pressed:border-[#9aaddd]"
                >
                  {product ? (
                    <DocsProductLogo product={product} className="size-3.5" />
                  ) : value === "symx" ? (
                    <CodeXml className="size-3.5" aria-hidden />
                  ) : (
                    <Layers className="size-3.5" aria-hidden />
                  )}
                  {label}
                </button>
              ))}
            </div>
            <DialogDescription className="sr-only">
              Search guides and reference across Libum documentation.
            </DialogDescription>
            <div className="relative mt-4">
              <Search
                className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted"
                aria-hidden
              />
              <Input
                ref={input}
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                aria-label="Search all documentation"
                placeholder={
                  activeScope === "symx"
                    ? "Search operations, fields, types, descriptions…"
                    : "Search guides, tasks, and more…"
                }
                type="search"
                autoComplete="off"
                spellCheck={false}
                className="mt-0 pl-9"
                onKeyDown={(event) => {
                  if (event.key === "ArrowDown" || event.key === "ArrowUp") {
                    const links =
                      resultsList.current?.querySelectorAll<HTMLAnchorElement>(
                        "a",
                      );
                    if (links?.length) {
                      event.preventDefault();
                      links[
                        event.key === "ArrowDown" ? 0 : links.length - 1
                      ].focus();
                    }
                  }
                }}
              />
            </div>
          </div>
          <div className="flex h-12 shrink-0 items-center justify-between gap-3 border-t px-5 text-xs text-muted">
            <span
              role="status"
              aria-live="polite"
              className="inline-flex items-center"
            >
              <SaveButtonSpinner show={apiPending} size="sm" margin="right" />
              {apiPending
                ? "Searching API reference…"
                : apiFailed && activeScope === "symx"
                  ? "API search unavailable. Try again."
                  : searching
                    ? `${visibleEntries.length}${visibleEntries.length === 20 ? "+" : ""} ${visibleEntries.length === 1 ? "result" : "results"}`
                    : query.trim()
                      ? "Type at least two characters"
                      : browsingApi
                        ? `${activeVersion === "v2022.01" ? "v2022.01" : "Persistent"} services`
                        : "Explore the docs"}
            </span>
            <div
              role="group"
              aria-label="API version"
              aria-hidden={activeScope !== "symx"}
              inert={activeScope !== "symx"}
              className={`shrink-0 transition-[opacity,transform] duration-200 ease-in-out motion-reduce:transition-none ${activeScope === "symx" ? "translate-x-0 opacity-100" : "pointer-events-none translate-x-2 opacity-0"}`}
            >
              <Select value={version} onValueChange={setVersion}>
                <SelectTrigger
                  aria-label="API version"
                  className="h-7 w-32 border-0 bg-sidebar-accent px-2 text-xs shadow-none"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All versions</SelectItem>
                  <SelectItem value="persistent">Persistent</SelectItem>
                  <SelectItem value="v2022.01">v2022.01</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="lb-scrollbar-overlay min-h-0 flex-1 px-2 pb-2">
            {!visibleEntries.length && (searching || activeScope === "symx") ? (
              <div className="flex min-h-full flex-col items-center justify-center px-5 py-8 text-center">
                <p className="text-sm font-medium">
                  {apiPending
                    ? "Searching…"
                    : apiFailed
                      ? "Search unavailable"
                      : searching
                        ? "No results found"
                        : "Search the SymXchange reference"}
                </p>
                <p className="mt-1 text-sm text-muted">
                  {activeScope === "symx"
                    ? "Search operation names, nested fields, descriptions, or schema types."
                    : "Try a product name, task, or shorter phrase."}
                </p>
              </div>
            ) : (
              <ul
                ref={resultsList}
                className="relative"
                aria-label="Documentation search results"
                onKeyDown={(event) => {
                  if (
                    !["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)
                  )
                    return;
                  const links = Array.from(
                    resultsList.current?.querySelectorAll<HTMLAnchorElement>(
                      "a",
                    ) ?? [],
                  );
                  const index = links.indexOf(
                    document.activeElement as HTMLAnchorElement,
                  );
                  if (index < 0) return;
                  event.preventDefault();
                  if (event.key === "ArrowUp" && index === 0) {
                    input.current?.focus({ preventScroll: true });
                    return;
                  }
                  const next =
                    event.key === "Home"
                      ? 0
                      : event.key === "End"
                        ? links.length - 1
                        : (index +
                            (event.key === "ArrowDown" ? 1 : -1) +
                            links.length) %
                          links.length;
                  links[next]?.focus();
                }}
              >
                {visibleEntries.map((item) => (
                  <li key={item.href} data-href={item.href}>
                    <Link
                      href={item.href}
                      onClick={() => {
                        navigating.current = true;
                        changeOpen(false);
                        if (isMobile) setOpenMobile(false);
                      }}
                      className="group flex gap-3 rounded-md p-3 transition-colors hover:bg-sidebar-accent focus-visible:bg-sidebar-accent"
                    >
                      <FileText
                        className="mt-1 size-4 shrink-0 text-muted"
                        aria-hidden
                      />
                      <div className="min-w-0 flex-1">
                        <span className="block text-xs text-muted">
                          {(item.breadcrumb ?? [item.product, item.title]).join(
                            " · ",
                          )}
                        </span>
                        <span className="block text-sm font-semibold text-gray-1100 dark:text-gray-100">
                          {item.heading}
                        </span>
                        {item.excerpt && (
                          <span className="mt-1 line-clamp-2 block text-sm text-foreground">
                            {item.excerpt}
                          </span>
                        )}
                      </div>
                      <ArrowUpRight
                        className="mt-1 size-4 shrink-0 text-muted"
                        aria-hidden
                      />
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="flex shrink-0 items-center justify-between border-t px-5 py-3 text-xs text-muted">
            <span>↑ ↓ to navigate · Enter to open</span>
            <span>Esc to close</span>
          </div>
        </DialogContent>
      </Dialog>
    </SearchContext.Provider>
  );
}
